import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';
import { getQuarterlyReport, SAMPLE_QUARTERLY_REPORT } from '../services/quarterlyReportService';
import type {
  QuarterlyReportBiomarkerRow,
  QuarterlyReportBorderlineRow,
  QuarterlyReportRecord,
} from '../types/quarterlyReport';
import { LoadFailureCard } from '../components/layout/memberPrimitives';
import { settle } from '../utils/dashboard/settle';

function formatDate(value?: any): string {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function QuarterlyReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<QuarterlyReportRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "Not released / stale link" and "the read failed" are different answers,
  // and they used to collapse into the same bare "Report not found." — the
  // second one tells a member their report is gone when it is only unreachable.
  const [failed, setFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const isSample = reportId === 'sample';

  useEffect(() => {
    const load = async () => {
      if (isSample) {
        setReport(SAMPLE_QUARTERLY_REPORT);
        setLoading(false);
        return;
      }
      if (!currentUser || !reportId) return;
      setLoading(true);
      setError(null);
      setFailed(false);
      const result = await settle(getQuarterlyReport(currentUser.uid, reportId));
      if (result.status === 'failed') {
        setFailed(true);
      } else if (!result.data) {
        setError("We couldn\u2019t find that report. It may have been unreleased, or the link is stale.");
      } else {
        setReport(result.data);
      }
      setLoading(false);
    };
    load();
  }, [currentUser, reportId, isSample, reloadToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/reports" className="text-sm underline" style={{ color: 'var(--melio-text-muted)' }}>
          Back to reports
        </Link>
        <div className="mt-6">
          <LoadFailureCard
            title="We couldn’t open this report"
            body="The report is still on file — this is a connection problem, not a change to your record."
            onRetry={() => setReloadToken((token) => token + 1)}
          />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/reports" className="text-sm underline" style={{ color: 'var(--melio-text-muted)' }}>
          Back to reports
        </Link>
        <p className="mt-6 text-sm" style={{ color: 'var(--melio-charcoal)' }}>
          {error || 'We couldn’t find that report.'}
        </p>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 py-10" style={{ color: 'var(--melio-charcoal)' }}>
      <div className="mb-6 flex items-center justify-between gap-3">
        <Link to="/reports" className="text-sm underline" style={{ color: 'var(--melio-text-muted)' }}>
          ← Back to reports
        </Link>
        {isSample && (
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(79, 93, 24, 0.12)', color: 'var(--melio-sage)' }}
          >
            Sample
          </span>
        )}
      </div>

      <header
        className="rounded-3xl border p-6 md:p-10 mb-6"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
      >
        <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
          Melio quarterly report
        </p>
        <h1 className="text-3xl md:text-4xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)" }}>
          {report.quarterLabel}
        </h1>
        <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: 'var(--melio-text-muted)' }}>
          {report.executiveSummary.headline}
        </p>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Meta term="Reviewing clinician" value={report.reviewingClinician} />
          <Meta term="Prepared by" value={report.preparedBy} />
          <Meta term="Tier" value={capitalise(report.membershipTier)} />
          {report.releasedAt && <Meta term="Released" value={formatDate(report.releasedAt)} />}
        </dl>
        {(report.reviewingClinician || report.signingClinician) && (
          <p className="text-sm mt-5 pt-4 border-t" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}>
            {formatReportAttribution(report.reviewingClinician, report.signingClinician)}
          </p>
        )}
      </header>

      <Section eyebrow="1" title="Executive summary">
        <h3 className="text-base font-medium mb-2">Most important conclusions</h3>
        <BulletList items={report.executiveSummary.conclusions} />
        <h3 className="text-base font-medium mt-6 mb-2">Top priorities for next quarter</h3>
        <OrderedList items={report.executiveSummary.topPriorities} />
      </Section>

      <Section eyebrow="2" title="Baseline context">
        <h3 className="text-base font-medium mb-2">Family history</h3>
        <BulletList items={report.baselineContext.familyHistoryBullets} />
        <h3 className="text-base font-medium mt-6 mb-2">Genetics context</h3>
        <BulletList items={report.baselineContext.geneticsBullets} />
        <h3 className="text-base font-medium mt-6 mb-2">Lifestyle / health context</h3>
        <BulletList items={report.baselineContext.lifestyleBullets} />
        <h3 className="text-base font-medium mt-6 mb-2">DNA-influenced decisions</h3>
        <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
          Your genetics result does not make a diagnosis on its own — it shapes the testing schedule and surveillance priorities. In your case, Melio used genetics and family history to:
        </p>
        <BulletList items={report.baselineContext.dnaInfluencedDecisions} />
      </Section>

      <Section eyebrow="3" title="What happened this quarter">
        <h3 className="text-base font-medium mb-2">Data received</h3>
        <BulletList items={report.whatHappened.dataReceived} />
        <h3 className="text-base font-medium mt-6 mb-3">Tests completed</h3>
        <BiomarkerTable rows={report.whatHappened.testsCompleted} />
        <BorderlineCarried rows={report.whatHappened.borderlineCarried} />
        {report.whatHappened.otherUpdates.length > 0 && (
          <>
            <h3 className="text-base font-medium mt-6 mb-2">Other notable updates</h3>
            <BulletList items={report.whatHappened.otherUpdates} />
          </>
        )}
      </Section>

      <Section eyebrow="4" title="Clinician interpretation">
        <div className="space-y-3 text-sm leading-relaxed">
          {report.clinicianInterpretation.map((paragraph, idx) => (
            <p key={idx}>{paragraph}</p>
          ))}
        </div>
      </Section>

      <Section eyebrow="5" title="Actions now">
        <h3 className="text-base font-medium mb-2">Recommended now</h3>
        <OrderedList items={report.actions.recommendedNow} />
        <h3 className="text-base font-medium mt-6 mb-2">Not recommended now</h3>
        <BulletList items={report.actions.notRecommendedNow} />
        <h3 className="text-base font-medium mt-6 mb-2">Escalate if</h3>
        <BulletList items={report.actions.escalateIf} />
      </Section>

      <Section eyebrow="6" title="Testing schedule ahead">
        <h3 className="text-base font-medium mb-2">Due next quarter</h3>
        <BulletList items={report.testingSchedule.nextQuarter} />
        <h3 className="text-base font-medium mt-6 mb-2">Due later this year</h3>
        <BulletList items={report.testingSchedule.laterThisYear} />
        <h3 className="text-base font-medium mt-6 mb-2">Not currently due</h3>
        <BulletList items={report.testingSchedule.notCurrentlyDue} />
      </Section>

      <Section eyebrow="7" title="Open issues / watchlist">
        <div className="space-y-4">
          {report.watchlist.map((item, idx) => (
            <div
              key={idx}
              className="rounded-2xl border p-4"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}
            >
              <p className="font-medium mb-1">{idx + 1}. {item.title}</p>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{item.detail}</p>
            </div>
          ))}
        </div>
        {report.nextReviewQuestion && (
          <>
            <h3 className="text-base font-medium mt-6 mb-2">Next review question</h3>
            <p className="text-sm italic" style={{ color: 'var(--melio-charcoal)' }}>
              &ldquo;{report.nextReviewQuestion}&rdquo;
            </p>
          </>
        )}
      </Section>

      {report.clinicianNote && (
        <footer
          className="rounded-3xl border p-6 md:p-8 mt-6"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'rgba(79, 93, 24, 0.08)' }}
        >
          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--melio-sage)' }}>
            Clinician note
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
            {report.clinicianNote}
          </p>
        </footer>
      )}
    </article>
  );
}

function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section
      className="rounded-3xl border p-6 md:p-8 mb-6"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          aria-hidden
          className="inline-flex items-center justify-center rounded-full text-xs font-medium"
          style={{ width: 28, height: 28, backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
        >
          {eyebrow}
        </span>
        <h2 className="text-xl md:text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
          {title}
        </h2>
      </div>
      <div style={{ color: 'var(--melio-charcoal)' }}>{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-1.5 text-sm list-disc pl-5">
      {items.map((item, idx) => (
        <li key={idx} style={{ color: 'var(--melio-charcoal)' }}>{item}</li>
      ))}
    </ul>
  );
}

function OrderedList({ items }: { items: string[] }) {
  if (!items?.length) return null;
  return (
    <ol className="space-y-1.5 text-sm list-decimal pl-5">
      {items.map((item, idx) => (
        <li key={idx} style={{ color: 'var(--melio-charcoal)' }}>{item}</li>
      ))}
    </ol>
  );
}

function BiomarkerTable({ rows }: { rows: QuarterlyReportBiomarkerRow[] }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ color: 'var(--melio-text-muted)' }}>
            <th className="text-left py-2 pr-4 font-medium">Test</th>
            <th className="text-left py-2 pr-4 font-medium">Result</th>
            <th className="text-left py-2 font-medium">Interpretation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} style={{ borderTop: '1px solid var(--melio-border)' }}>
              <td className="py-2 pr-4" style={{ color: 'var(--melio-charcoal)' }}>{row.test}</td>
              <td className="py-2 pr-4" style={{ color: 'var(--melio-charcoal)' }}>{row.result}</td>
              <td className="py-2" style={{ color: 'var(--melio-text-muted)' }}>{row.interpretation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Tier 4 results, called out rather than left in the table.
 *
 * The Abnormal Results Pathway gives Tier 4 no clock of its own precisely
 * because this report is the channel that carries it — so a borderline finding
 * that only appears as one row among thirty has been communicated in name only.
 *
 * The framing sentence is deliberately flat. "Borderline" is the word people
 * hear as either "nothing" or "the beginning of something", and the pathway's
 * own description — a normal variant, or an early change — is the honest answer
 * to both readings.
 */
function BorderlineCarried({ rows }: { rows?: QuarterlyReportBorderlineRow[] }) {
  if (!rows?.length) return null;
  return (
    <div
      className="mt-6 rounded-2xl border p-4"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}
    >
      <h3 className="text-base font-medium mb-1">Results we are watching</h3>
      <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
        These sit close to the edge of the usual range. That may be a normal variation for you, or
        an early change worth following — which is why they are here rather than acted on now. Your
        clinician will go through them with you.
      </p>
      <ul className="space-y-2 text-sm">
        {rows.map((row, idx) => (
          <li key={idx} style={{ color: 'var(--melio-charcoal)' }}>
            <span className="font-medium">{row.test}</span>
            {row.result ? ` — ${row.result}` : ''}
            {row.rationale && (
              <span style={{ color: 'var(--melio-text-muted)' }}>{` · ${row.rationale}`}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Meta({ term, value }: { term: string; value?: string }) {
  if (!value?.trim()) return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest" style={{ color: 'var(--melio-text-muted)' }}>{term}</dt>
      <dd className="mt-0.5" style={{ color: 'var(--melio-charcoal)' }}>{value}</dd>
    </div>
  );
}

function formatReportAttribution(reviewingClinician?: string, signingClinician?: string): string {
  const reviewed = reviewingClinician?.trim();
  const signedOff = signingClinician?.trim();
  if (reviewed && signedOff) {
    return `Reviewed by ${reviewed} · signed off by ${signedOff}`;
  }
  if (reviewed) return `Reviewed by ${reviewed}`;
  if (signedOff) return `Signed off by ${signedOff}`;
  return '';
}

function capitalise(value: string) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}
